import React, { useState } from "react";

import { FormikErrors } from "formik";

import {
  DestinationStatusEnum,
  DestinationTypeEnum,
} from "../../../../enums/destination";
import { WordPressDestination } from "../../../../models";
import { mask } from "../../utils/formValidationUtils";

import Button from "../../../../components/form/Button";
import Input from "../../../../components/form/Input";
import PostDelayHourSelector from "../common/PostDelayHourSelector";
import RandPostHourSelector from "../common/RandPostHourSelector";
import { StatusSelector } from "../common/StatusSelector";
import WordPressGuideModal from "../modals/WordPressGuideModal";

interface WordpressProps {
  values: WordPressDestination;
  errors: FormikErrors<WordPressDestination>;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

const WordPress: React.FC<WordpressProps> = ({
  values,
  errors,
  setFieldValue,
}) => {
  const [passwordGuide, setPasswordGuide] = useState(false);

  return (
    <div className="py-4 px-4 sm:px-6 flex flex-col gap-8 mb-4">
      <div className="flex flex-col md:flex-row gap-6">
        <Input
          id="name"
          label="Destination Name"
          name="name"
          type="text"
          required
          placeholder="Name your destination"
          className="max-w-md"
          tooltip="Name your destination (e.g., My WordPress Site)"
          value={values.name}
          errorMessage={errors?.name}
          errorPosition="bottom-left"
          onChange={(e: any) => setFieldValue("name", e.target.value)}
        />

        <Input
          id="url"
          label="URL"
          name="destination.url"
          type="text"
          required
          placeholder="https://mydomain.com"
          className="max-w-md"
          tooltip="The root domain for your WordPress Site (e.g. https://mydomain.com)"
          value={values.destination.url}
          errorMessage={errors?.destination?.url}
          errorPosition="bottom-left"
          onChange={(e: any) =>
            setFieldValue("destination.url", e.target.value)
          }
        />
      </div>
      <div className="flex flex-col md:flex-row gap-6">
        <Input
          id="username"
          label="Admin Login Username"
          name="destination.username"
          type="text"
          required
          placeholder="Your WordPress username"
          className="max-w-md"
          tooltip="Your WordPress username"
          value={values.destination.username}
          errorMessage={errors?.destination?.username}
          errorPosition="bottom-left"
          onChange={(e: any) =>
            setFieldValue("destination.username", e.target.value)
          }
        />
        <div className="relative w-full">
          <Input
            id="secretKey"
            label="App Password"
            name="secretKey"
            type="text"
            required
            placeholder="App Password (Not your WordPress login password)"
            className="max-w-md"
            tooltip="IMPORTANT: This is not your WordPress login password, rather a separate password generated for API usage."
            value={values.destination.secretKey}
            errorMessage={errors?.destination?.secretKey}
            errorPosition="bottom-left"
            fontMono
            onChange={(e: any) => {
              setFieldValue("destination.secretKey", e.target.value);
            }}
            onFocus={(e: any) => {
              if (e.target.value.includes(mask)) {
                setFieldValue("destination.secretKey", "");
              }
            }}
          />
          <Button
            paddingZero
            type="button"
            variant="transparent-primary"
            className="absolute mt-1"
            onClick={() => setPasswordGuide(!passwordGuide)}
          >
            How to generate
          </Button>
        </div>
      </div>
      {passwordGuide && (
        <WordPressGuideModal onRequestClose={() => setPasswordGuide(false)} />
      )}

      <div className="flex flex-col md:flex-row gap-6">
        <Input
          id="authorId"
          label="Author ID"
          name="destination.authorId"
          type="number"
          tooltip='This is the WordPress Author ID (a number) to whom you want the posts associated with. You can access this ID using a WordPress plugin such as "Reveal IDs."'
          placeholder="Optional. WordPress Author ID (a number)"
          className="max-w-md"
          value={values?.destination?.authorId || ""}
          errorMessage={errors?.destination?.authorId}
          errorPosition="bottom-left"
          onChange={(e: any) =>
            setFieldValue("destination.authorId", e.target.value)
          }
        />

        <Input
          id="categoryId"
          label="Category ID"
          name="destination.categoryId"
          type="number"
          tooltip='This is the WordPress Category ID (a number) to assign your posts a category. You can access this ID using a WordPress plugin such as "Reveal IDs."'
          placeholder="Optional. WordPress Category ID (a number)"
          className="max-w-md"
          value={values?.destination?.categoryId || ""}
          errorMessage={errors?.destination?.categoryId}
          errorPosition="bottom-left"
          onChange={(e: any) =>
            setFieldValue("destination.categoryId", e.target.value)
          }
        />
      </div>
      <Input
        id="postType"
        label="Custom post type slug"
        name="destination.postType"
        type="text"
        maxLength={20}
        placeholder='Optional. Leave blank if you are unsure and articles will be posted to the default "posts"'
        tooltip='Use the post type slug.  A custom post type in WordPress is a way to create content types that are different from standard posts and pages. The default post type for posting blog articles is "posts".'
        value={values.destination.postType ?? ""}
        errorMessage={errors?.destination?.postType}
        errorPosition="bottom-left"
        onChange={(e: any) => {
          let inputValue = e.target.value
            .toLowerCase()
            .replace(/[^a-z0-9_-]/g, "")
            .slice(0, 20);
          setFieldValue("destination.postType", inputValue);
        }}
      />
      <StatusSelector
        status={values?.destination.status}
        onChange={(status: string) =>
          setFieldValue("destination.status", status)
        }
        platform={DestinationTypeEnum.WORDPRESS}
      />
      {values?.destination.status === DestinationStatusEnum.FUTURE && (
        <div className="flex flex-col gap-8 ml-6">
          <PostDelayHourSelector
            postDelayHours={values?.destination?.postDelayHours}
            onChange={(value: number | null) =>
              setFieldValue("destination.postDelayHours", value)
            }
          />
          <RandPostHourSelector
            randomPostHours={values?.destination?.randomPostHours}
            onChange={(value: number | null) =>
              setFieldValue("destination.randomPostHours", value)
            }
          />
        </div>
      )}
    </div>
  );
};

export default WordPress;
